import { Scene, PerspectiveCamera, WebGLRenderer, AmbientLight, DirectionalLight, Vector3, LinearSRGBColorSpace } from 'three';
import Coin, { MIN_COIN_SCALE, MAX_COIN_SCALE, END_RANGE_MULTIPLIER, END_RANGE_MULTIPLIER_PORTRAIT, MIN_PEAK_Y_MULTIPLIER, MAX_PEAK_Y_MULTIPLIER, MIN_PEAK_Y_MULTIPLIER_PORTRAIT, MAX_PEAK_Y_MULTIPLIER_PORTRAIT } from './Coin.js';
import CoinTextureManager from './CoinTextureManager.js';

// Constants
const CAMERA_FOV = 50;
const CAMERA_NEAR = 0.1;
const CAMERA_FAR = 1000;
const CAMERA_Z = 5;
const MIN_COINS = 1;
const MAX_COINS = 20;
const BOTTOM_OFFSET_PX = 80;
const AMBIENT_LIGHT_INTENSITY = 2;
const DIRECTIONAL_LIGHT_INTENSITY = 1.5;
const DIRECTIONAL_LIGHT_X = 0;
const DIRECTIONAL_LIGHT_Y = 0;
const DIRECTIONAL_LIGHT_Z = 1;
const MAX_DEVICE_PIXEL_RATIO = 2;
const COIN_APPEAR_INTERVAL = 0.11; // seconds between each coin appearing
const START_POSITION_RANGE_MULTIPLIER = 0.2; // Start position range as fraction of viewport width
const START_POSITION_RANGE_MULTIPLIER_PORTRAIT = 0.4; // Start position range for portrait orientation
const NO_ALTERNATE_CHANCE = 0.1; // Chance to not alternate side placement
const PORTRAIT_SCALE_MULTIPLIER = 0.85; // Scale multiplier for portrait orientation
const VISIBLE_HEIGHT_MULTIPLIER = 2; // Multiplier for visible height calculation
const DEGREES_TO_RADIANS = Math.PI / 180; // Conversion factor
const CAMERA_POSITION_Y = 0; // Camera Y position
const CAMERA_LOOK_AT_Y = 0; // Camera look at Y position
const CAMERA_LOOK_AT_X = 0; // Camera look at X position
const CAMERA_LOOK_AT_Z = 0; // Camera look at Z position

export default class SceneManager {
  constructor(canvas, coinCount) {
    this.canvas = canvas;
    this.coinCount = Math.max(MIN_COINS, Math.min(coinCount || MIN_COINS, MAX_COINS));
    this.scene = null;
    this.camera = null;
    this.renderer = null;
    this.coins = [];
    this.isDestroyed = false;
    this.canvasRect = null;
    this.textureManager = new CoinTextureManager();
    this.toastElement = null;
  }

  init() {
    // Force a reflow to ensure canvas has updated size in DOM
    void this.canvas.offsetWidth;
    this.canvasRect = this.canvas.getBoundingClientRect();

    // Ensure we have valid dimensions
    if (this.canvasRect.width === 0 || this.canvasRect.height === 0) {
      this.canvasRect = {
        width: window.innerWidth,
        height: window.innerHeight,
      };
    }

    // Create scene with transparent background
    this.scene = new Scene();
    this.scene.background = null; // Transparent background

    // Create camera
    const aspect = this.canvasRect.width / this.canvasRect.height;
    this.camera = new PerspectiveCamera(CAMERA_FOV, aspect, CAMERA_NEAR, CAMERA_FAR);
    this.camera.position.z = CAMERA_Z;
    this.camera.position.y = CAMERA_POSITION_Y;
    this.camera.lookAt(CAMERA_LOOK_AT_X, CAMERA_LOOK_AT_Y, CAMERA_LOOK_AT_Z);

    // Create renderer with transparency
    this.renderer = new WebGLRenderer({
      canvas: this.canvas,
      antialias: true,
			alpha: true,
			backgroundColor: 0x000000,
			colorSpace: LinearSRGBColorSpace,
    });
    this.renderer.setClearColor(0x000000, 0); // Transparent clear color
    // setSize updates the canvas's internal width/height attributes
    // The third parameter (false) means don't update CSS style, which is correct since CSS handles sizing
    this.renderer.setSize(this.canvasRect.width, this.canvasRect.height, false);
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, MAX_DEVICE_PIXEL_RATIO));

    // Add lighting (not needed for MeshBasicMaterial, but keeping for consistency)
    const ambientLight = new AmbientLight(0xffffff, AMBIENT_LIGHT_INTENSITY);
    this.scene.add(ambientLight);

    const directionalLight = new DirectionalLight(0xffffff, DIRECTIONAL_LIGHT_INTENSITY);
    directionalLight.position.set(DIRECTIONAL_LIGHT_X, DIRECTIONAL_LIGHT_Y, DIRECTIONAL_LIGHT_Z);
    this.scene.add(directionalLight);

    // Coins will be created separately when toast is registered
  }

  createCoins() {
    // Clear any existing coins
    if (this.coins.length > 0) {
      this.coins.forEach((coin) => {
        coin.dispose();
        if (coin.mesh && coin.mesh.parent) {
          coin.mesh.parent.remove(coin.mesh);
        }
      });
      this.coins = [];
    }

    if (!this.scene || !this.canvas) {
      return;
    }

    // Force a reflow and get fresh canvas dimensions
    void this.canvas.offsetWidth;
    this.canvasRect = this.canvas.getBoundingClientRect();

    // Ensure we have valid dimensions
    if (this.canvasRect.width === 0 || this.canvasRect.height === 0) {
      this.canvasRect = {
        width: window.innerWidth,
        height: window.innerHeight,
      };
    }

    // Update camera aspect ratio with fresh dimensions
    const aspect = this.canvasRect.width / this.canvasRect.height;
    if (this.camera) {
      this.camera.aspect = aspect;
      this.camera.updateProjectionMatrix();
    }

    const fovRad = this.camera.fov * DEGREES_TO_RADIANS;
    const visibleHeight = VISIBLE_HEIGHT_MULTIPLIER * CAMERA_Z * Math.tan(fovRad / 2);
    const visibleWidth = visibleHeight * (this.canvasRect.width / this.canvasRect.height);
    const bottomY = -visibleHeight / 2;
    const topY = visibleHeight / 2;

    const offsetY = (BOTTOM_OFFSET_PX / this.canvasRect.height) * visibleHeight;

    // Calculate coin origin based on toast position
    let coinX = 0; // Default to center
    let coinY = bottomY + offsetY; // Default to bottom with offset

    if (this.toastElement) {
      const toastRect = this.toastElement.getBoundingClientRect();

      // Find the toast container to determine position
      let container = this.toastElement.closest('.advanced-toast-container');
      if (!container) {
        // Fallback: find parent with position class
        container = this.toastElement.parentElement;
        while (container && !container.classList.contains('advanced-toast-container')) {
          container = container.parentElement;
        }
      }

      if (container) {
        // Determine position from container class
        const isTop = container.classList.contains('advanced-toast-container--top-right') ||
                      container.classList.contains('advanced-toast-container--top-left') ||
                      container.classList.contains('advanced-toast-container--top-center');
        const isBottom = container.classList.contains('advanced-toast-container--bottom-right') ||
                         container.classList.contains('advanced-toast-container--bottom-left') ||
                         container.classList.contains('advanced-toast-container--bottom-center');
        const isRight = container.classList.contains('advanced-toast-container--top-right') ||
                        container.classList.contains('advanced-toast-container--bottom-right');
        const isLeft = container.classList.contains('advanced-toast-container--top-left') ||
                       container.classList.contains('advanced-toast-container--bottom-left');
        const isCenter = container.classList.contains('advanced-toast-container--top-center') ||
                         container.classList.contains('advanced-toast-container--bottom-center');

        // Calculate Y position (convert from screen to world coordinates) and add offset
        if (isTop) {
          const screenY = toastRect.top;
          const normalizedY = 1 - (screenY / this.canvasRect.height); // 0 = bottom, 1 = top
          coinY = bottomY + (normalizedY * visibleHeight) + offsetY;
        } else if (isBottom) {
          const screenY = toastRect.bottom;
          const normalizedY = 1 - (screenY / this.canvasRect.height); // 0 = bottom, 1 = top
          coinY = bottomY + (normalizedY * visibleHeight) + offsetY;
        }

        // Calculate X position - always use toast center
        const toastCenterX = toastRect.left + toastRect.width / 2;
        const normalizedX = (toastCenterX / this.canvasRect.width) * 2 - 1; // -1 to 1
        coinX = normalizedX * (visibleWidth / 2);
      }
    }

    // Detect portrait orientation
    const isPortrait = this.canvasRect.height > this.canvasRect.width;

    const viewportInfo = {
      visibleWidth,
      visibleHeight,
      bottomY,
      startY: coinY,
    };

    // Calculate coin scale based on count
    // Scale interpolates from MAX_COIN_SCALE to MIN_COIN_SCALE based on coin count
    const clampedCount = Math.max(MIN_COINS, Math.min(this.coinCount, MAX_COINS));
    const t = (clampedCount - MIN_COINS) / (MAX_COINS - MIN_COINS);
    let coinScale = MAX_COIN_SCALE + (MIN_COIN_SCALE - MAX_COIN_SCALE) * t;

    // Apply portrait scale reduction if in portrait orientation
    if (isPortrait) {
      coinScale *= PORTRAIT_SCALE_MULTIPLIER;
    }

    // Calculate random X position range as fraction of viewport width, centered on coin origin
    // Use larger range for portrait orientation
    const startPositionMultiplier = isPortrait ? START_POSITION_RANGE_MULTIPLIER_PORTRAIT : START_POSITION_RANGE_MULTIPLIER;
    const xRange = visibleWidth * startPositionMultiplier;

    // Use larger end range multiplier for portrait orientation
    const endRangeMultiplier = isPortrait ? END_RANGE_MULTIPLIER_PORTRAIT : END_RANGE_MULTIPLIER;

    // Use wider peak Y range for portrait orientation
    const minPeakYMultiplier = isPortrait ? MIN_PEAK_Y_MULTIPLIER_PORTRAIT : MIN_PEAK_Y_MULTIPLIER;
    const maxPeakYMultiplier = isPortrait ? MAX_PEAK_Y_MULTIPLIER_PORTRAIT : MAX_PEAK_Y_MULTIPLIER;

    // Track side for non-alternating cases
    let isLeft = false;

    // Create coins and trigger staggered animations
    for (let i = 0; i < this.coinCount; i++) {
      // Chance to not alternate (use same side as previous)
      isLeft = Math.random() < NO_ALTERNATE_CHANCE ? isLeft : (i % 2 === 0);

      // Random X position within the left or right half of the range, centered on coin origin
      const randomX = coinX + (Math.random() * (xRange / 2)) * (isLeft ? -1 : 1);

      const coinPosition = new Vector3(randomX, coinY, 0);

      const coin = new Coin({
        scene: this.scene,
        position: coinPosition,
        viewportInfo,
        textureManager: this.textureManager,
        coinScale,
        endRangeMultiplier,
        minPeakYMultiplier,
        maxPeakYMultiplier,
      });
      this.coins.push(coin);

      // Trigger appearance animation with interval
      const delay = i * COIN_APPEAR_INTERVAL;
      coin.animateAppearance(delay);
    }
  }

  render() {
    if (this.isDestroyed || !this.scene || !this.camera || !this.renderer) return;

    // Update coins
    this.coins.forEach((coin) => {
      coin.update();
    });

    // Render the scene
    this.renderer.render(this.scene, this.camera);
  }

  resize() {
    if (!this.renderer || !this.camera) return;

    // Force a reflow to ensure canvas has updated size in DOM
    void this.canvas.offsetWidth;

    // Get canvas dimensions - use clientWidth/clientHeight which reflects CSS sizing
    const oldRect = this.canvasRect ? { ...this.canvasRect } : null;
    this.canvasRect = this.canvas.getBoundingClientRect();

    // Check if size actually changed (compare with renderer's internal size)
    if (this.canvasRect.width > 0 && this.canvasRect.height > 0 && (this.renderer.domElement.width !== this.canvasRect.width || this.renderer.domElement.height !== this.canvasRect.height)) {
      this.camera.aspect = this.canvasRect.width / this.canvasRect.height;
      this.camera.updateProjectionMatrix();
      // setSize updates the canvas's internal width/height attributes
      // The third parameter (false) means don't update CSS style
      this.renderer.setSize(this.canvasRect.width, this.canvasRect.height, false);
      this.renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, MAX_DEVICE_PIXEL_RATIO));

      // Recreate coins if orientation changed (portrait/landscape) or size changed significantly
      const wasPortrait = oldRect ? oldRect.height > oldRect.width : false;
      const isPortrait = this.canvasRect.height > this.canvasRect.width;
      const orientationChanged = wasPortrait !== isPortrait;

      if (orientationChanged && this.coins.length > 0) {
        this.createCoins();
      }
    }
  }

  destroy() {
    this.isDestroyed = true;

    if (this.renderer) {
      this.renderer.dispose();
      this.renderer = null;
    }

    if (this.coins.length > 0) {
      this.coins.forEach((coin) => {
        coin.dispose();
        // Remove mesh from scene
        if (coin.mesh && coin.mesh.parent) {
          coin.mesh.parent.remove(coin.mesh);
        }
      });
      this.coins = [];
    }

    this.scene = null;
    this.camera = null;
  }
}

